
import { toast } from "../hooks/use-toast";

export type FinancialDataType = 'balance-sheet' | 'profit-loss' | 'cash-flow' | 'debt-profile';
export type EntityType = 'CIN' | 'LLP';

export interface CSVTemplateConfig {
  filename: string;
  headers: string[];
  sampleData: Record<string, string | number>[];
}

// Define template configurations for each financial data type
const templates: Record<FinancialDataType, CSVTemplateConfig> = {
  'balance-sheet': {
    filename: 'balance_sheet_template.csv',
    headers: ['Line Item', 'FY 22-23', 'FY 21-22', 'FY 20-21'],
    sampleData: [
      { 'Line Item': 'ASSETS', 'FY 22-23': '', 'FY 21-22': '', 'FY 20-21': '' },
      { 'Line Item': 'Tangible Assets', 'FY 22-23': 1939300000, 'FY 21-22': 1844800000, 'FY 20-21': 1755600000 },
      { 'Line Item': 'Producing Properties', 'FY 22-23': '—', 'FY 21-22': '—', 'FY 20-21': '—' },
      { 'Line Item': 'Intangible Assets', 'FY 22-23': 151800000, 'FY 21-22': 144200000, 'FY 20-21': 137100000 },
      { 'Line Item': 'Preproducing Properties', 'FY 22-23': '—', 'FY 21-22': '—', 'FY 20-21': '—' },
      { 'Line Item': 'Tangible Assets - Capital Work in Progress', 'FY 22-23': 981900000, 'FY 21-22': 933800000, 'FY 20-21': 887100000 },
      { 'Line Item': 'Intangible Assets Under Development', 'FY 22-23': 8500000, 'FY 21-22': 8100000, 'FY 20-21': 7700000 },
      { 'Line Item': 'Non-Current Investments', 'FY 22-23': 21404000000, 'FY 21-22': 20333800000, 'FY 20-21': 19317100000 },
      { 'Line Item': 'Deferred Tax Assets (Net)', 'FY 22-23': 1028400000, 'FY 21-22': 977000000, 'FY 20-21': 928200000 },
      { 'Line Item': 'Foreign Currency Monetary Item Translation Difference Asset Account', 'FY 22-23': '—', 'FY 21-22': '—', 'FY 20-21': '—' },
      { 'Line Item': 'Long-Term Loans and Advances', 'FY 22-23': 11547600000, 'FY 21-22': 10970200000, 'FY 20-21': 10421700000 },
      { 'Line Item': 'Other Non-Current Assets', 'FY 22-23': 1212200000, 'FY 21-22': 1151600000, 'FY 20-21': 1094000000 },
      { 'Line Item': 'Current Investments', 'FY 22-23': 9461500000, 'FY 21-22': 8988400000, 'FY 20-21': 8539000000 },
      { 'Line Item': 'Inventories', 'FY 22-23': 60061300000, 'FY 21-22': 57058200000, 'FY 20-21': 54205300000 },
      { 'Line Item': 'Trade Receivables', 'FY 22-23': 1566500000, 'FY 21-22': 1488300000, 'FY 20-21': 1413900000 },
      { 'Line Item': 'Cash and Bank Balances', 'FY 22-23': 16162100000, 'FY 21-22': 15354000000, 'FY 20-21': 14586300000 },
      { 'Line Item': 'Short-Term Loans and Advances', 'FY 22-23': 68963700000, 'FY 21-22': 65515500000, 'FY 20-21': 62239700000 },
      { 'Line Item': 'Other Current Assets', 'FY 22-23': 4912300000, 'FY 21-22': 4667000000, 'FY 20-21': 4433700000 },
      { 'Line Item': 'Total Assets', 'FY 22-23': 199401100000, 'FY 21-22': 189534900000, 'FY 20-21': 180058200000 },
      { 'Line Item': 'LIABILITIES & EQUITY', 'FY 22-23': '', 'FY 21-22': '', 'FY 20-21': '' },
      { 'Line Item': 'Share Capital', 'FY 22-23': 1390100000, 'FY 21-22': 1390100000, 'FY 20-21': 1390100000 },
      { 'Line Item': 'Reserves and Surplus', 'FY 22-23': 98061200000, 'FY 21-22': 93258100000, 'FY 20-21': 88695200000 },
      { 'Line Item': 'Money Received Against Share Warrants', 'FY 22-23': '—', 'FY 21-22': '—', 'FY 20-21': '—' },
      { 'Line Item': 'Share Application Money Pending Allotment', 'FY 22-23': '—', 'FY 21-22': '—', 'FY 20-21': '—' },
      { 'Line Item': 'Deferred Government Grants', 'FY 22-23': '—', 'FY 21-22': '—', 'FY 20-21': '—' },
      { 'Line Item': 'Minority Interest', 'FY 22-23': '—', 'FY 21-22': '—', 'FY 20-21': '—' },
      { 'Line Item': 'Long-Term Borrowings', 'FY 22-23': 0, 'FY 21-22': 0, 'FY 20-21': 0 },
      { 'Line Item': 'Deferred Tax Liabilities (Net)', 'FY 22-23': '—', 'FY 21-22': '—', 'FY 20-21': '—' },
      { 'Line Item': 'Foreign Currency Monetary Item Translation Difference Liability Account', 'FY 22-23': '—', 'FY 21-22': '—', 'FY 20-21': '—' },
      { 'Line Item': 'Other Long-Term Liabilities', 'FY 22-23': 113600000, 'FY 21-22': 108000000, 'FY 20-21': 102600000 },
      { 'Line Item': 'Long-Term Provisions', 'FY 22-23': 145900000, 'FY 21-22': 138700000, 'FY 20-21': 131800000 },
      { 'Line Item': 'Short-Term Borrowings', 'FY 22-23': 64001000000, 'FY 21-22': 60801000000, 'FY 20-21': 57761000000 },
      { 'Line Item': 'Trade Payables', 'FY 22-23': 14204800000, 'FY 21-22': 13494600000, 'FY 20-21': 12819900000 },
      { 'Line Item': 'Other Current Liabilities', 'FY 22-23': 20931700000, 'FY 21-22': 19887700000, 'FY 20-21': 18893300000 },
      { 'Line Item': 'Short-Term Provisions', 'FY 22-23': 552800000, 'FY 21-22': 525200000, 'FY 20-21': 498900000 },
      { 'Line Item': 'Total Liabilities', 'FY 22-23': 199401100000, 'FY 21-22': 189534900000, 'FY 20-21': 180058200000 },
      { 'Line Item': 'REVENUE', 'FY 22-23': '', 'FY 21-22': '', 'FY 20-21': '' },
      { 'Line Item': 'Net Revenue', 'FY 22-23': 11550500000, 'FY 21-22': 10972700000, 'FY 20-21': 10424100000 },
      { 'Line Item': 'EXPENSES', 'FY 22-23': '', 'FY 21-22': '', 'FY 20-21': '' },
      { 'Line Item': 'Total Cost of Materials Consumed', 'FY 22-23': 41697600000, 'FY 21-22': 39612200000, 'FY 20-21': 37631600000 },
      { 'Line Item': 'Total Purchases of Stock in Trade', 'FY 22-23': '—', 'FY 21-22': '—', 'FY 20-21': '—' },
      { 'Line Item': 'Total Changes in Inventories or Finished Goods', 'FY 22-23': -36829400000, 'FY 21-22': -34987900000, 'FY 20-21': -33238500000 },
      { 'Line Item': 'Total Employee Benefit Expense', 'FY 22-23': 1781800000, 'FY 21-22': 1692700000, 'FY 20-21': 1608100000 },
      { 'Line Item': 'Total Other Expenses', 'FY 22-23': 3524200000, 'FY 21-22': 3347500000, 'FY 20-21': 3180100000 },
      { 'Line Item': 'Operating Profit', 'FY 22-23': 1376300000, 'FY 21-22': 1307300000, 'FY 20-21': 1242400000 },
      { 'Line Item': 'Other Income', 'FY 22-23': 9450000000, 'FY 21-22': 8977500000, 'FY 20-21': 8528600000 },
      { 'Line Item': 'Depreciation', 'FY 22-23': 189600000, 'FY 21-22': 180200000, 'FY 20-21': 171200000 },
      { 'Line Item': 'Profit Before Interest and Tax', 'FY 22-23': 10636700000, 'FY 21-22': 10104600000, 'FY 20-21': 9599800000 },
      { 'Line Item': 'Interest', 'FY 22-23': 2331300000, 'FY 21-22': 2214700000, 'FY 20-21': 2104000000 },
      { 'Line Item': 'Profit Before Tax and Exceptional Items Before Tax', 'FY 22-23': 2331300000, 'FY 21-22': 2214700000, 'FY 20-21': 2104000000 },
      { 'Line Item': 'Exceptional Items Before Tax', 'FY 22-23': 2331300000, 'FY 21-22': 2214700000, 'FY 20-21': 2104000000 },
      { 'Line Item': 'Profit Before Tax', 'FY 22-23': 8305400000, 'FY 21-22': 7889900000, 'FY 20-21': 7495800000 },
      { 'Line Item': 'Income Tax', 'FY 22-23': 1748700000, 'FY 21-22': 1661300000, 'FY 20-21': 1577200000 },
      { 'Line Item': 'Profit for Period from Continuing Operations', 'FY 22-23': 6556700000, 'FY 21-22': 6228600000, 'FY 20-21': 5918600000 },
      { 'Line Item': 'Profit from Discontinuing Operation After Tax', 'FY 22-23': 6556700000, 'FY 21-22': 6228600000, 'FY 20-21': 5918600000 },
      { 'Line Item': 'Minority Interest and Profit from Associates and Joint Ventures', 'FY 22-23': 6556700000, 'FY 21-22': 6228600000, 'FY 20-21': 5918600000 },
      { 'Line Item': 'Profit After Tax', 'FY 22-23': 6556700000, 'FY 21-22': 6228600000, 'FY 20-21': 5918600000 }

    ]
  },
  'profit-loss': {
    filename: 'profit_loss_template.csv',
    headers: ['Line Item', 'FY 22-23', 'FY 21-22', 'FY 20-21'],
    sampleData: [
      { 'Line Item': 'REVENUE', 'FY 22-23': '', 'FY 21-22': '', 'FY 20-21': '' },
      { 'Line Item': 'Net Revenue', 'FY 22-23': 11550500000, 'FY 21-22': 10972700000, 'FY 20-21': 10424100000 },
      { 'Line Item': 'Revenue from Operations', 'FY 22-23': 11550500000, 'FY 21-22': 10972700000, 'FY 20-21': 10424100000 },
      { 'Line Item': 'Revenue from Interest', 'FY 22-23': '—', 'FY 21-22': '—', 'FY 20-21': '—' },
      { 'Line Item': 'Revenue from Other Financial Services', 'FY 22-23': '—', 'FY 21-22': '—', 'FY 20-21': '—' },
      { 'Line Item': 'EXPENSES', 'FY 22-23': '', 'FY 21-22': '', 'FY 20-21': '' },
      { 'Line Item': 'Total Cost of Materials Consumed', 'FY 22-23': 41697600000, 'FY 21-22': 39612200000, 'FY 20-21': 37631600000 },
      { 'Line Item': 'Total Purchases of Stock in Trade', 'FY 22-23': '—', 'FY 21-22': '—', 'FY 20-21': '—' },
      { 'Line Item': 'Total Changes in Inventories or Finished Goods', 'FY 22-23': -36829400000, 'FY 21-22': -34987900000, 'FY 20-21': -33238500000 },
      { 'Line Item': 'Total Employee Benefit Expense', 'FY 22-23': 1781800000, 'FY 21-22': 1692700000, 'FY 20-21': 1608100000 },
      { 'Line Item': 'Total Other Expenses', 'FY 22-23': 3524200000, 'FY 21-22': 3347500000, 'FY 20-21': 3180100000 },
      { 'Line Item': 'Operating Profit', 'FY 22-23': 1376300000, 'FY 21-22': 1307300000, 'FY 20-21': 1242400000 },
      { 'Line Item': 'Other Income', 'FY 22-23': 9450000000, 'FY 21-22': 8977500000, 'FY 20-21': 8528600000 },
      { 'Line Item': 'Depreciation', 'FY 22-23': 189600000, 'FY 21-22': 180200000, 'FY 20-21': 171200000 },
      { 'Line Item': 'Profit Before Interest and Tax', 'FY 22-23': 10636700000, 'FY 21-22': 10104600000, 'FY 20-21': 9599800000 },
      { 'Line Item': 'Interest', 'FY 22-23': 2331300000, 'FY 21-22': 2214700000, 'FY 20-21': 2104000000 },
      { 'Line Item': 'Profit Before Tax', 'FY 22-23': 8305400000, 'FY 21-22': 7889900000, 'FY 20-21': 7495800000 },
      { 'Line Item': 'Income Tax', 'FY 22-23': 1748700000, 'FY 21-22': 1661300000, 'FY 20-21': 1577200000 },
      { 'Line Item': 'Profit After Tax', 'FY 22-23': 6556700000, 'FY 21-22': 6228600000, 'FY 20-21': 5918600000 }
    ]
  },
  'cash-flow': {
    filename: 'cash_flow_template.csv',
    headers: ['Line Item', 'FY 22-23', 'FY 21-22', 'FY 20-21'],
    sampleData: [
      { 'Line Item': 'OPERATING ACTIVITIES', 'FY 22-23': '', 'FY 21-22': '', 'FY 20-21': '' },
      { 'Line Item': 'Profit Before Tax', 'FY 22-23': 8305400000, 'FY 21-22': 7889900000, 'FY 20-21': 7495800000 },
      { 'Line Item': 'Adjustment for Finance Cost and Depreciation', 'FY 22-23': 2520800000, 'FY 21-22': 2394900000, 'FY 20-21': 2275200000 },
      { 'Line Item': 'Adjustment for Current and Non-Current Assets', 'FY 22-23': -37494900000, 'FY 21-22': -35620100000, 'FY 20-21': -33839100000 },
      { 'Line Item': 'Adjustment for Current and Non-Current Liabilities', 'FY 22-23': 19069700000, 'FY 21-22': 18116200000, 'FY 20-21': 17210400000 },
      { 'Line Item': 'Other Adjustments in Operating Activities', 'FY 22-23': -11345600000, 'FY 21-22': -10778300000, 'FY 20-21': -10239400000 },
      { 'Line Item': 'Cash Flows from/(Used in) Operating Activities', 'FY 22-23': -18944600000, 'FY 21-22': -17997400000, 'FY 20-21': -17097100000 },
      { 'Line Item': 'INVESTING ACTIVITIES', 'FY 22-23': '', 'FY 21-22': '', 'FY 20-21': '' },
      { 'Line Item': 'Cash Outflow from Purchase of Assets', 'FY 22-23': 4499600000, 'FY 21-22': 4274600000, 'FY 20-21': 4060900000 },
      { 'Line Item': 'Cash Inflow from Sale of Assets', 'FY 22-23': 25572700000, 'FY 21-22': 24294100000, 'FY 20-21': 23079400000 },
      { 'Line Item': 'Income from Assets', 'FY 22-23': 2749200000, 'FY 21-22': 2611700000, 'FY 20-21': 2481100000 },
      { 'Line Item': 'Other Adjustments in Investing Activities', 'FY 22-23': -10770200000, 'FY 21-22': -10231700000, 'FY 20-21': -9720100000 },
      { 'Line Item': 'Cash Flows from/(Used in) Investing Activities', 'FY 22-23': 13052100000, 'FY 21-22': 12399500000, 'FY 20-21': 11779500000 },
      { 'Line Item': 'FINANCING ACTIVITIES', 'FY 22-23': '', 'FY 21-22': '', 'FY 20-21': '' },
      { 'Line Item': 'Cash Outflow from Repayment of Capital and Borrowings', 'FY 22-23': 60900000, 'FY 21-22': 57900000, 'FY 20-21': 55000000 },
      { 'Line Item': 'Cash Inflow from Raising Capital and Borrowings', 'FY 22-23': 12278700000, 'FY 21-22': 11664800000, 'FY 20-21': 11081600000 },
      { 'Line Item': 'Interest and Dividends Paid', 'FY 22-23': 3790900000, 'FY 21-22': 3601400000, 'FY 20-21': 3423300000 },
      { 'Line Item': 'Other Adjustments in Financing Activities', 'FY 22-23': -2200000, 'FY 21-22': -2100000, 'FY 20-21': -2000000 },
      { 'Line Item': 'Cash Flows from/(Used in) Financing Activities', 'FY 22-23': 8424700000, 'FY 21-22': 8003400000, 'FY 20-21': 7601300000 },
      { 'Line Item': 'Net Increase/(Decrease) in Cash & Cash Equivalents', 'FY 22-23': 2532200000, 'FY 21-22': 2405500000, 'FY 20-21': 2283700000 },
      { 'Line Item': 'Cash & Cash Equivalents at End of Period', 'FY 22-23': 3858900000, 'FY 21-22': 3665900000, 'FY 20-21': 3484100000 }
    ]
  },
  'debt-profile': {
    filename: 'debt_profile_template.csv',
    headers: ['nature','loanType','bankName','totalLoanAmount','emi','loanStartDate','roi','outstandingAsOnDate','totalTenure'],
    sampleData: [
    {'nature':'Business' , 'loanType':'Loan','bankName':'Secured','totalLoanAmount':1000000,'emi':25000,'loanStartDate':'15-03-2024','roi':12.5,'outstandingAsOnDate':750000,'totalTenure':48},
      {'nature':'Working Capital','loanType':'Unsecured','bankName':'ICICI Bank','totalLoanAmount':500000,'emi':15000,'loanStartDate':'01-02-2024','roi':14,'outstandingAsOnDate':450000,'totalTenure':36}
      ]
  }
};

// Combined LLP Template (Balance Sheet + Profit & Loss)
const llpCombinedTemplate: CSVTemplateConfig = {
  filename: 'llp_financials_template.csv',
  headers: ['Line Item', 'FY 22-23', 'FY 21-22', 'FY 20-21'],
  sampleData: [
    // Balance Sheet - Assets
    { 'Line Item': 'ASSETS', 'FY 22-23': '', 'FY 21-22': '', 'FY 20-21': '' },
    { 'Line Item': 'Gross Fixed Assets', 'FY 22-23': 309812152, 'FY 21-22': 0, 'FY 20-21': 0 },
    { 'Line Item': 'Depreciation and Amortization', 'FY 22-23': 34823725, 'FY 21-22': 0, 'FY 20-21': 0 },
    { 'Line Item': 'Net Fixed Assets', 'FY 22-23': 274988427, 'FY 21-22': 0, 'FY 20-21': 0 },
    { 'Line Item': 'Investments', 'FY 22-23': 250000000, 'FY 21-22': 0, 'FY 20-21': 0 },
    { 'Line Item': 'Loans and Advances', 'FY 22-23': 52052705, 'FY 21-22': 0, 'FY 20-21': 0 },
    { 'Line Item': 'Inventories', 'FY 22-23': 40477963, 'FY 21-22': 0, 'FY 20-21': 0 },
    { 'Line Item': 'Trade Receivables', 'FY 22-23': 110219836, 'FY 21-22': 0, 'FY 20-21': 0 },
    { 'Line Item': 'Cash and Cash Equivalents', 'FY 22-23': 1185225293, 'FY 21-22': 0, 'FY 20-21': 0 },
    { 'Line Item': 'Other Assets', 'FY 22-23': 105885241, 'FY 21-22': 0, 'FY 20-21': 0 },
    { 'Line Item': 'Total Assets', 'FY 22-23': 2018849465, 'FY 21-22': 0, 'FY 20-21': 0 },
    // Balance Sheet - Partner's Funds
    { 'Line Item': 'PARTNER\'S FUNDS', 'FY 22-23': '', 'FY 21-22': '', 'FY 20-21': '' },
    { 'Line Item': 'Contribution Received', 'FY 22-23': 1550000000, 'FY 21-22': 0, 'FY 20-21': 0 },
    { 'Line Item': 'Reserves and Surplus', 'FY 22-23': 0, 'FY 21-22': 0, 'FY 20-21': 0 },
    // Balance Sheet - Liabilities
    { 'Line Item': 'LIABILITIES', 'FY 22-23': '', 'FY 21-22': '', 'FY 20-21': '' },
    { 'Line Item': 'Secured Loan', 'FY 22-23': 0, 'FY 21-22': 0, 'FY 20-21': 0 },
    { 'Line Item': 'Unsecured Loan', 'FY 22-23': 0, 'FY 21-22': 0, 'FY 20-21': 0 },
    { 'Line Item': 'Short Term Borrowing', 'FY 22-23': 103658926, 'FY 21-22': 0, 'FY 20-21': 0 },
    { 'Line Item': 'Trade Payables', 'FY 22-23': 60864030, 'FY 21-22': 0, 'FY 20-21': 0 },
    { 'Line Item': 'Other Liabilities', 'FY 22-23': 268852175, 'FY 21-22': 0, 'FY 20-21': 0 },
    { 'Line Item': 'Other Provisions', 'FY 22-23': 225000, 'FY 21-22': 0, 'FY 20-21': 0 },
    // Balance Sheet - Provisions
    { 'Line Item': 'PROVISIONS', 'FY 22-23': '', 'FY 21-22': '', 'FY 20-21': '' },
    { 'Line Item': 'Provisions for Taxation', 'FY 22-23': 35249334, 'FY 21-22': 0, 'FY 20-21': 0 },
    { 'Line Item': 'Provisions for Contingencies', 'FY 22-23': 0, 'FY 21-22': 0, 'FY 20-21': 0 },
    { 'Line Item': 'Provisions for Insurance', 'FY 22-23': 0, 'FY 21-22': 0, 'FY 20-21': 0 },
    { 'Line Item': 'Total Liabilities', 'FY 22-23': 2018849465, 'FY 21-22': 0, 'FY 20-21': 0 },
    // Profit & Loss - Revenue
    { 'Line Item': 'REVENUE', 'FY 22-23': '', 'FY 21-22': '', 'FY 20-21': '' },
    { 'Line Item': 'Net Revenue', 'FY 22-23': 1355333317, 'FY 21-22': 0, 'FY 20-21': 0 },
    // Profit & Loss - Expenses
    { 'Line Item': 'EXPENSES', 'FY 22-23': '', 'FY 21-22': '', 'FY 20-21': '' },
    { 'Line Item': 'Operating Cost', 'FY 22-23': 1088310105, 'FY 21-22': 0, 'FY 20-21': 0 },
    { 'Line Item': 'Total Cost of Materials Consumed', 'FY 22-23': 497006505, 'FY 21-22': 0, 'FY 20-21': 0 },
    { 'Line Item': 'Total Purchases of Stock in Trade', 'FY 22-23': 0, 'FY 21-22': 0, 'FY 20-21': 0 },
    { 'Line Item': 'Total Changes in Inventories or Finished Goods', 'FY 22-23': -17112128, 'FY 21-22': 0, 'FY 20-21': 0 },
    { 'Line Item': 'Total Employee Benefit Expense', 'FY 22-23': 192085917, 'FY 21-22': 0, 'FY 20-21': 0 },
    { 'Line Item': 'Total Other Expenses', 'FY 22-23': 416329811, 'FY 21-22': 0, 'FY 20-21': 0 },
    { 'Line Item': 'Operating Profit', 'FY 22-23': 267023212, 'FY 21-22': 0, 'FY 20-21': 0 },
    { 'Line Item': 'Other Income', 'FY 22-23': 55197719, 'FY 21-22': 0, 'FY 20-21': 0 },
    { 'Line Item': 'Depreciation', 'FY 22-23': 34823725, 'FY 21-22': 0, 'FY 20-21': 0 },
    { 'Line Item': 'Profit Before Interest and Tax', 'FY 22-23': 287397206, 'FY 21-22': 0, 'FY 20-21': 0 },
    { 'Line Item': 'Interest', 'FY 22-23': 186587047, 'FY 21-22': 0, 'FY 20-21': 0 },
    { 'Line Item': 'Profit Before Tax and Exceptional Items Before Tax', 'FY 22-23': 100810159, 'FY 21-22': 0, 'FY 20-21': 0 },
    { 'Line Item': 'Exceptional Items Before Tax', 'FY 22-23': '', 'FY 21-22': '', 'FY 20-21': '' },
    { 'Line Item': 'Profit Before Tax', 'FY 22-23': 100810159, 'FY 21-22': 0, 'FY 20-21': 0 },
    { 'Line Item': 'Income Tax', 'FY 22-23': 35319089, 'FY 21-22': 0, 'FY 20-21': 0 },
    { 'Line Item': 'Profit for Period from Continuing Operations', 'FY 22-23': 65491070, 'FY 21-22': 0, 'FY 20-21': 0 },
    { 'Line Item': 'Profit from Discontinuing Operation After Tax', 'FY 22-23': '', 'FY 21-22': '', 'FY 20-21': '' },
    { 'Line Item': 'Minority Interest and Profit from Associates and Joint Ventures', 'FY 22-23': '', 'FY 21-22': '', 'FY 20-21': '' },
    { 'Line Item': 'Profit After Tax', 'FY 22-23': 65491070, 'FY 21-22': 0, 'FY 20-21': 0 }
  ]
};


export const getCSVTemplate = (type: FinancialDataType, entityType: EntityType = 'CIN'): CSVTemplateConfig => {
  // For LLP, always return combined template (Balance Sheet + Profit & Loss)
  if (entityType === 'LLP' && (type === 'balance-sheet' || type === 'profit-loss')) {
    return llpCombinedTemplate;
  }
  return templates[type];
};

// Convert data to CSV format
export const generateCSV = (config: CSVTemplateConfig): string => {
  const headers = config.headers.join(',');
  const rows = config.sampleData.map((row) => {
    return config.headers.map((header) => {
      const value = row[header];
      // Add quotes around string values if they contain commas
      if (typeof value === 'string' && value.includes(',')) {
        return `"${value}"`;
      }
      return value !== undefined ? value : '';
    }).join(',');
  }).join('\n');
  
  return `${headers}\n${rows}`;
};

// Download CSV file
export const downloadCSV = (type: FinancialDataType, entityType: EntityType = 'CIN'): void => {
  try {
    const template = getCSVTemplate(type, entityType);
    const csvContent = generateCSV(template);
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', template.filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast({
      title: "Template Downloaded",
      description: `${template.filename} has been downloaded successfully.`
    });
  } catch (error) {
    console.error("Error downloading CSV:", error);
    toast({
      title: "Download Failed",
      description: "There was an error downloading the template. Please try again.",
      variant: "destructive"
    });
  }
};

// Parse CSV content
export const parseCSV = (content: string): { headers: string[]; data: Record<string, string | number>[]; } => {
  const lines = content.trim().split('\n');
  const headers = lines[0].split(',').map(header => header.trim());
  
  const data = lines.slice(1).map(line => {
    const values = parseCSVLine(line);
    return headers.reduce((obj, header, index) => {
      const value = values[index] || '';
      // Try to convert numeric values
      obj[header] = !isNaN(Number(value)) && value !== '' ? Number(value) : value;
      return obj;
    }, {} as Record<string, string | number>);
  });
  
  return { headers, data };
};

// Helper function to parse CSV line considering quoted values
const parseCSVLine = (line: string): string[] => {
  const values: string[] = [];
  let currentValue = '';
  let insideQuotes = false;
  
  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    
    if (char === '"' && (i === 0 || line[i-1] !== '\\')) {
      insideQuotes = !insideQuotes;
    } else if (char === ',' && !insideQuotes) {
      values.push(currentValue.trim());
      currentValue = '';
    } else {
      currentValue += char;
    }
  }
  
  values.push(currentValue.trim());
  return values;
};

// Validate uploaded CSV against template
export const validateCSV = (type: FinancialDataType, parsedCSV: { headers: string[]; data: Record<string, string | number>[]; }, entityType: EntityType = 'CIN'): { isValid: boolean; errors: string[] } => {
  const template = getCSVTemplate(type, entityType);
  const errors: string[] = [];
  
  const expectedHeaders = template.headers;
  const actualHeaders = parsedCSV.headers;
  
  if (expectedHeaders.length !== actualHeaders.length) {
    errors.push(`Expected ${expectedHeaders.length} columns, but found ${actualHeaders.length}`);
  }
  
  for (let i = 0; i < expectedHeaders.length; i++) {
    if (i >= actualHeaders.length || actualHeaders[i] !== expectedHeaders[i]) {
      errors.push(`Expected column "${expectedHeaders[i]}" at position ${i+1}, but found "${actualHeaders[i] || 'nothing'}"`);
    }
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

// Mapping from CSV line items to LLP field names
const llpBalanceSheetMapping: Record<string, { section: 'assets' | 'liabilities' | 'subTotals', field: string }> = {
  'Gross Fixed Assets': { section: 'assets', field: 'gross_fixed_assets' },
  'Depreciation and Amortization': { section: 'assets', field: 'depreciation_and_amortization' },
  'Net Fixed Assets': { section: 'assets', field: 'net_fixed_assets' },
  'Investments': { section: 'assets', field: 'investments' },
  'Loans and Advances': { section: 'assets', field: 'loans_and_advances' },
  'Inventories': { section: 'assets', field: 'inventories' },
  'Trade Receivables': { section: 'assets', field: 'trade_receivables' },
  'Cash and Cash Equivalents': { section: 'assets', field: 'cash_and_cash_equivalents' },
  'Other Assets': { section: 'assets', field: 'other_assets' },
  'Total Assets': { section: 'subTotals', field: 'given_assets_total' },
  'Contribution Received': { section: 'liabilities', field: 'contribution_received' },
  'Reserves and Surplus': { section: 'liabilities', field: 'reserves_and_surplus' },
  'Secured Loan': { section: 'liabilities', field: 'secured_loan' },
  'Unsecured Loan': { section: 'liabilities', field: 'unsecured_loan' },
  'Short Term Borrowing': { section: 'liabilities', field: 'short_term_borrowing' },
  'Trade Payables': { section: 'liabilities', field: 'trade_payables' },
  'Other Liabilities': { section: 'liabilities', field: 'other_liabilities' },
  'Other Provisions': { section: 'liabilities', field: 'other_provisions' },
  'Provisions for Taxation': { section: 'liabilities', field: 'provisions_for_taxation' },
  'Provisions for Contingencies': { section: 'liabilities', field: 'provisions_for_contingencies' },
  'Provisions for Insurance': { section: 'liabilities', field: 'provisions_for_insurance' },
  'Total Liabilities': { section: 'subTotals', field: 'given_liabilities_total' }
};

const llpProfitLossMapping: Record<string, string> = {
  'Net Revenue': 'net_revenue',
  'Operating Cost': 'operating_cost',
  'Total Cost of Materials Consumed': 'total_cost_of_materials_consumed',
  'Total Purchases of Stock in Trade': 'total_purchases_of_stock_in_trade',
  'Total Changes in Inventories or Finished Goods': 'total_changes_in_inventories_or_finished_goods',
  'Total Employee Benefit Expense': 'total_employee_benefit_expense',
  'Total Other Expenses': 'total_other_expenses',
  'Operating Profit': 'operating_profit',
  'Other Income': 'other_income',
  'Depreciation': 'depreciation',
  'Profit Before Interest and Tax': 'profit_before_interest_and_tax',
  'Interest': 'interest',
  'Profit Before Tax and Exceptional Items Before Tax': 'profit_before_tax_and_exceptional_items_before_tax',
  'Exceptional Items Before Tax': 'exceptional_items_before_tax',
  'Profit Before Tax': 'profit_before_tax',
  'Income Tax': 'income_tax',
  'Profit for Period from Continuing Operations': 'profit_for_period_from_continuing_operations',
  'Profit from Discontinuing Operation After Tax': 'profit_from_discontinuing_operation_after_tax',
  'Minority Interest and Profit from Associates and Joint Ventures': 'minority_interest_and_profit_from_associates_and_joint_ventures',
  'Profit After Tax': 'profit_after_tax'
};

// Convert CSV data to LLP JSON format
// Note: type parameter is kept for backward compatibility but not used since we process both sections
export const convertCSVToLLPFormat = (
  _type: 'balance-sheet' | 'profit-loss',
  csvData: Record<string, string | number>[],
  headers: string[]
): any[] => {
  // Extract year columns (skip first column which is 'Line Item')
  const yearColumns = headers.slice(1);
  
  // Group data by year
  const yearData: Record<string, any> = {};
  
  yearColumns.forEach((yearHeader) => {
    const financialRecord: any = {
      year: '', // Will be set based on year header format
      stated_on: '',
      statement_of_assets_and_liabilities: {
        assets: {},
        liabilities: {},
        subTotals: {},
        metadata: {}
      },
      statement_of_income_and_expenditure: {
        lineItems: {},
        revenue_breakup: {},
        depreciation_breakup: {},
        metadata: {}
      }
    };
    
    // Process all rows to populate both balance sheet and profit & loss
    csvData.forEach((row) => {
      const lineItem = String(row['Line Item'] || '').trim();
      
      // Skip empty rows and section headers
      if (!lineItem || lineItem === '' || 
          lineItem === 'ASSETS' || lineItem === 'PARTNER\'S FUNDS' || 
          lineItem === 'LIABILITIES' || lineItem === 'PROVISIONS' ||
          lineItem === 'REVENUE' || lineItem === 'EXPENSES') {
        return;
      }
      
      // Process Balance Sheet items
      const bsMapping = llpBalanceSheetMapping[lineItem];
      if (bsMapping) {
        const value = row[yearHeader];
        const numValue = typeof value === 'number' ? value : (value === '' || value === '—' ? 0 : Number(value) || 0);
        
        if (bsMapping.section === 'subTotals') {
          financialRecord.statement_of_assets_and_liabilities.subTotals[bsMapping.field] = numValue;
        } else {
          financialRecord.statement_of_assets_and_liabilities[bsMapping.section][bsMapping.field] = numValue;
        }
      }
      
      // Process Profit & Loss items
      const pnlFieldName = llpProfitLossMapping[lineItem];
      if (pnlFieldName) {
        const value = row[yearHeader];
        const numValue = typeof value === 'number' ? value : (value === '' || value === '—' ? null : Number(value) || null);
        financialRecord.statement_of_income_and_expenditure.lineItems[pnlFieldName] = numValue;
      }
    });
    
    // Parse year from header (e.g., "FY 23-24" -> year: "2024-03-31", stated_on: "2025-03-31")
    const yearMatch = yearHeader.match(/FY\s+(\d{2})-(\d{2})/);
    if (yearMatch) {
      const endYear = 2000 + parseInt(yearMatch[2]);
      financialRecord.year = `${endYear}-03-31`;
      financialRecord.stated_on = `${endYear + 1}-03-31`;
    } else {
      // Try other year formats or use header as-is
      // If header is already in YYYY-MM-DD format, use it
      const dateMatch = yearHeader.match(/(\d{4})-(\d{2})-(\d{2})/);
      if (dateMatch) {
        const year = parseInt(dateMatch[1]);
        financialRecord.year = yearHeader;
        financialRecord.stated_on = `${year + 1}-03-31`;
      } else {
        financialRecord.year = yearHeader;
        financialRecord.stated_on = yearHeader;
      }
    }
    
    yearData[yearHeader] = financialRecord;
  });
  
  return Object.values(yearData);
};
